import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import Button from '../../components/Button';
import Form, { Field, FieldArray, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Modal, { ModalActionFooter, ModalTitle } from '../../components/Modal';
import SeriesPostManager from './SeriesPostManager';
import updatePostSeriesCache from './updatePostSeriesCache';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import ConfirmationModal from '../../components/ConfirmationModal';

gql(`
  fragment EditSeriesModal_series on Series {
    id
    name
    posts {
      id
      ...SeriesPostManager_posts
    }
  }
`);

const DELETE_SERIES = gql(/** @complexity 2 */ `
  mutation deleteSeries($id: ID!) {
    deleteSeries(id: $id) {
      id
    }
  }
`);

const UPDATE_SERIES = gql(/** @complexity 10 */ `
  mutation EditSeriesModalUpdateSeries(
    $id: ID!
    $name: String!
    $postIds: [ID]!
  ) {
    updateSeries(id: $id, name: $name, postIds: $postIds) {
      ...EditSeriesModal_series
    }
  }
`);

const EDIT_SERIES_MODAL_QUERY = gql(/** @complexity 5 */ `
  query EditSeriesModal($id: ID!) {
    series(id: $id) {
      ...EditSeriesModal_series
    }
  }
`);

interface EditSeriesModalProps {
  close: () => void;
  seriesId: string;
}

type FormValues = {
  name: string;
  posts: DocumentType<typeof EDIT_SERIES_MODAL_QUERY>['series']['posts'];
};

const EditSeriesModal = ({ close, seriesId }: EditSeriesModalProps) => {
  const dispatch = useAppDispatch();

  const { data, loading } = useQuery(EDIT_SERIES_MODAL_QUERY, {
    variables: { id: seriesId },
  });

  const series = data?.series;

  const [updateSeries] = useMutation(UPDATE_SERIES, {
    update: updatePostSeriesCache(series?.posts ?? [], 'updateSeries'),
  });

  const [deleteSeries] = useMutation(DELETE_SERIES, {
    update: (cache) => {
      cache.evict({
        id: cache.identify({ __typename: 'Series', id: seriesId }),
      });
      cache.gc();
    },
  });

  const onSubmit = series
    ? ({ name, posts }: FormValues) =>
        updateSeries({
          variables: {
            id: series.id,
            name,
            postIds: posts.map(({ id }) => id),
          },
        }).then(() => dispatch(UIActions.closeModal({ reset: true })))
    : undefined;

  const onDelete = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Delete this series?',
        message: (
          <p>
            Deleted series are gone forever. Their posts will remain in their
            current topics.
          </p>
        ),
        confirmButtonText: 'Delete',
        onConfirm: async () => {
          if (!series) {
            return;
          }
          await deleteSeries({ variables: { id: series.id } });
          dispatch(UIActions.closeModal({ reset: true }));
        },
      }),
    );
  };

  if (loading || !series) {
    return <LoadingIndicator delay={0} card />;
  }

  return (
    <Modal>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          name: series.name,
          posts: series.posts,
        }}
      >
        {({ values }) => (
          <>
            <ModalTitle close={close}>Edit series</ModalTitle>
            <Field
              autoFocus
              label="Name"
              name="name"
              required
              type="text"
              autoComplete="off"
            />
            <FieldArray name="posts">
              {(arrayHelpers) => (
                <SeriesPostManager
                  posts={values.posts}
                  seriesId={series.id}
                  onAdd={arrayHelpers.push}
                  onMove={arrayHelpers.move}
                  onRemove={arrayHelpers.remove}
                />
              )}
            </FieldArray>
            <ModalActionFooter
              secondary={
                <Button danger onClick={onDelete}>
                  Delete series
                </Button>
              }
              primary={<Submit size="large">Save</Submit>}
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditSeriesModal;
